import React, { useCallback, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Event from '@mui/icons-material/Event';
import Divider from '@mui/material/Divider';
import { ReactComponent as MoneyIcon } from '../../assets/icons/money_icon.svg';
import { formatMoney } from '../../helpers';
import { useShoppingCart, useIsMobile } from '../../hooks';
import { IconTooltip } from '../tooltips';
import { capitalizeFirstLetterWithoutToLowerCase } from '../../helpers/text-parser';
import {
  PRIMARY_STACK_CLASS_NAME,
  SUMMARY_PREVIEW_INFO_LIST_STACK_CLASS_NAME,
  SUMMARY_PREVIEW_INFO_ITEM_STACK_CLASS_NAME,
} from '../../theme/constants/stack';

const CART_TYPE_MAPPER = {
  'available-factoring': 'facturas',
  'available-offers': 'facturas',
};

const ShoppingCartInfo = ({ title, value, loading, isMobile }) => (
  <Stack
    direction="column"
    spacing={0.5}
    className={SUMMARY_PREVIEW_INFO_ITEM_STACK_CLASS_NAME}
  >
    <Typography variant={isMobile ? 'small_2' : 'small_3'} fontWeight={500} color="white">
      {title}
    </Typography>
    {loading || !value ? (
      <Skeleton
        variant="text"
        sx={{
          height: { xs: 20, md: 30 },
        }}
      />
    ) : (
      <Typography variant={isMobile ? 'small_2' : 'medium_3'} fontWeight={600} color="white">
        {value}
      </Typography>
    )}
  </Stack>
);

ShoppingCartInfo.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loading: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

ShoppingCartInfo.defaultProps = {
  value: null,
};

const SummaryPreview = ({ cartType, onActionClick }) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { shoppingCartData, disabledShowCart, loading } = useShoppingCart({ cartType });

  const handleOnClick = useCallback(() => {
    onActionClick(shoppingCartData);
  }, [onActionClick, shoppingCartData]);

  const previewInfo = useMemo(
    () => [
      { title: `${capitalizeFirstLetterWithoutToLowerCase(CART_TYPE_MAPPER[cartType])}`, value: shoppingCartData?.invoiceCount },
      {
        title: 'Clientes',
        value: shoppingCartData?.clientCount,
      },
      {
        title: t(
          isMobile
            ? `summary-preview-amount-title-mobile-${cartType}`
            : `summary-preview-amount-title-${cartType}`,
          { defaultValue: `Monto total ${isMobile ? '' : CART_TYPE_MAPPER[cartType]}` },
        ),
        value: `${formatMoney(shoppingCartData?.totalAmount, true)}`,
      },
    ],
    [cartType, shoppingCartData, isMobile, t],
  );

  const defaultButtonText = cartType === 'available-offers' ? t('offer action') : 'Cotizar';
  const buttonText = t(`summary-preview-button-text-${cartType}`, {
    defaultValue: defaultButtonText,
  });
  const mobileButtonText = t(`summary-preview-button-text-mobile-${cartType}`, {
    defaultValue: buttonText,
  });

  return (
    <Stack
      width="100%"
      borderRadius={2}
      py={isMobile ? 1.5 : 2.5}
      px={isMobile ? 1 : 4}
      direction="row"
      className={PRIMARY_STACK_CLASS_NAME}
      justifyContent="space-between"
      spacing={isMobile ? 0.5 : 2}
      alignItems="center"
      sx={{
        width: '100%',
        minWidth: 325,
        minHeight: { xs: 86, md: 97 },
      }}
    >
      <Stack
        direction="row"
        spacing={isMobile ? 1 : 5}
        justifyContent="flex-start"
        className={SUMMARY_PREVIEW_INFO_LIST_STACK_CLASS_NAME}
      >
        {previewInfo.map(({ title, value }, idx) => (
          <Fragment key={title}>
            <ShoppingCartInfo title={title} value={value} loading={loading} isMobile={isMobile} />
            {idx < previewInfo.length - 1 && !isMobile && (
              <Divider orientation="vertical" flexItem sx={{ borderColor: 'white', borderWidth: 1 }} />
            )}
          </Fragment>
        ))}
      </Stack>
      <IconTooltip
        icon={<Event />}
        text="Recuerda ingresar las fechas de pago"
        disabledHover={!disabledShowCart}
      >
        {isMobile ? (
          <Button
            id="preview-show-cart"
            variant="secondary"
            size="small"
            onClick={handleOnClick}
            disabled={disabledShowCart || loading}
            sx={{
              px: { xs: 1, md: 2 },
              fontSize: { xs: '12px', md: '14px' },
              maxWidth: { xs: '80px', md: 'auto' },
            }}
          >
            {mobileButtonText}
          </Button>
        ) : (
          <Button
            id={`preview-show-cart-${cartType}`}
            startIcon={<MoneyIcon />}
            variant="secondary"
            size="small"
            onClick={handleOnClick}
            disabled={disabledShowCart || loading || !shoppingCartData}
          >
            {buttonText}
          </Button>
        )}
      </IconTooltip>
    </Stack>
  );
};

SummaryPreview.propTypes = {
  cartType: PropTypes.string.isRequired,
  onActionClick: PropTypes.func.isRequired,
};

export default SummaryPreview;
